import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { usePrefersReducedMotion } from '@/lib/utils';
import './Sparkles.css';

/** O intervalo (ms) entre um nascimento e o próximo, sorteado a cada vez entre os dois. */
export const SPARKLE_BIRTH_MIN_MS = 900;
export const SPARKLE_BIRTH_MAX_MS = 1800;

/**
 * Quanto cada faísca vive (ms), sorteado entre os dois: é a duração da animação e
 * o timer que a tira da lista. A vida mais curta passa do maior intervalo entre
 * nascimentos, então, a partir da primeira, a pílula nunca fica sem nenhuma.
 */
export const SPARKLE_LIFE_MIN_MS = 2400;
export const SPARKLE_LIFE_MAX_MS = 4000;

/** Mais que cinco ao mesmo tempo deixa de ser brilho e vira confete sobre a pílula. */
export const SPARKLE_MAX_ALIVE = 5;

/** O tamanho (px) de cada faísca, sorteado entre os dois. */
export const SPARKLE_MIN_SIZE = 16;
export const SPARKLE_MAX_SIZE = 30;

/** As cores do brilho: o branco da marca, o lilás, o turquesa e o amarelo de IA. */
export const SPARKLE_COLORS = [
  'var(--brand-ink)',
  'var(--brand-light)',
  'var(--info)',
  'var(--highlight)',
] as const;

/** Até quantos px além da borda da pílula pode cair o centro de uma faísca: ela ainda a cobre. */
const SLACK_PX = 4;

/**
 * O quadrado (px) do canto superior direito da caixa, onde mora o "Ocultar" da
 * área logada (28px, 8px para fora). Nenhum centro cai nele, e nem a maior faísca
 * alcança o botão: a 36px da borda, os 15px de meia largura param antes dele.
 */
const HIDE_CORNER_PX = 36;

/** Sorteios de posição antes do ponto de reserva (só uma sorte muito ruim chega lá). */
const PLACEMENT_TRIES = 12;

/** Uma faísca na tela: o centro (CSS, na caixa da pílula), o tamanho, a cor e a vida. */
export interface Sparkle {
  id: number;
  x: string;
  y: string;
  size: number;
  color: string;
  /** Duração (ms) da animação e do timer que a remove; as paradas não têm. */
  life?: number;
}

/**
 * Com movimento reduzido: três faíscas paradas e acesas, montadas na borda da
 * pílula (em cima, embaixo e na ponta esquerda) para nunca tapar o rótulo, em
 * pontos que tocam tanto a pílula larga quanto o círculo do celular.
 */
export const RESTING_SPARKLES: readonly Sparkle[] = [
  { id: 0, x: '30%', y: '2px', size: 18, color: SPARKLE_COLORS[3] },
  { id: 1, x: '58%', y: 'calc(100% - 2px)', size: 22, color: SPARKLE_COLORS[1] },
  { id: 2, x: '4px', y: '62%', size: 18, color: SPARKLE_COLORS[2] },
];

interface Box {
  width: number;
  height: number;
}

/** O valor entre `min` e `max` na fração `r` do sorteio. */
const between = (min: number, max: number, r: number): number => min + (max - min) * r;

/**
 * O centro de uma faísca, em px a partir do canto superior esquerdo da caixa da
 * pílula. A pílula é um estádio (no celular, um círculo), não o retângulo da
 * caixa: só valem pontos a até SLACK_PX da forma pintada — no canto da caixa a
 * faísca flutuaria solta além da ponta arredondada. E nenhum cai no canto do
 * "Ocultar".
 */
export function sparkleCenter(box: Box, random: () => number): { x: number; y: number } {
  const radius = box.height / 2;
  for (let tries = 0; tries < PLACEMENT_TRIES; tries += 1) {
    const x = Math.round(between(-SLACK_PX, box.width + SLACK_PX, random()));
    const y = Math.round(between(-SLACK_PX, box.height + SLACK_PX, random()));
    // A distância ao eixo da pílula: o segmento entre os centros das duas pontas.
    const axisX = Math.min(Math.max(x, radius), box.width - radius);
    const onPill = Math.hypot(x - axisX, y - radius) <= radius + SLACK_PX;
    const underHide = box.width - x < HIDE_CORNER_PX && y < HIDE_CORNER_PX;
    if (onPill && !underHide) return { x, y };
  }
  // Sem sorte, ou sem medida (a caixa fora do layout): um ponto do quarto
  // esquerdo, embaixo, que cai na pílula em qualquer largura.
  return { x: Math.round(box.width / 4), y: Math.round((box.height * 3) / 4) };
}

/** Uma faísca nova na caixa medida: ponto, tamanho, cor e vida, tudo sorteado. */
function bornSparkle(id: number, box: Box, random: () => number): Required<Sparkle> {
  const { x, y } = sparkleCenter(box, random);
  const colorIndex = Math.min(SPARKLE_COLORS.length - 1, Math.floor(random() * SPARKLE_COLORS.length));
  return {
    id,
    x: `${x}px`,
    y: `${y}px`,
    size: Math.round(between(SPARKLE_MIN_SIZE, SPARKLE_MAX_SIZE, random())),
    color: SPARKLE_COLORS[colorIndex]!,
    life: Math.round(between(SPARKLE_LIFE_MIN_MS, SPARKLE_LIFE_MAX_MS, random())),
  };
}

export interface SparklesProps {
  /** A fonte do sorteio, em [0, 1): os testes injetam uma com semente. */
  random?: () => number;
}

/**
 * As faíscas de IA sobre a pílula fechada — a da área logada e a do site:
 * brilhos de quatro pontas que nascem em momentos, pontos, tamanhos e cores
 * sorteados, crescem e, no instante em que chegam ao tamanho cheio, começam a
 * sumir; até cinco vivas. A camada cobre a caixa da pílula, à frente do botão, e
 * é decoração pura (`aria-hidden`, sem ponteiro): nunca rouba o clique, o
 * arraste nem o "Ocultar". Com a aba oculta para de nascer (nada roda sem
 * ninguém ver) e, com `prefers-reduced-motion`, ficam três paradas.
 */
export function Sparkles({ random = Math.random }: SparklesProps): JSX.Element {
  const reduced = usePrefersReducedMotion();
  const layerRef = useRef<HTMLSpanElement>(null);
  const [alive, setAlive] = useState<readonly Sparkle[]>([]);

  useEffect(() => {
    if (reduced) return undefined;
    // Os timers decidem pelo campo daqui, na hora; o estado só espelha a tela.
    let field: Sparkle[] = [];
    let serial = 0;
    let birthTimer: ReturnType<typeof setTimeout> | undefined;
    // Um nascimento que vence com o campo cheio acontece na próxima morte: o teto
    // nunca deixa a pílula sem nenhuma.
    let owed = false;
    const deaths = new Set<ReturnType<typeof setTimeout>>();

    const show = (next: Sparkle[]): void => {
      field = next;
      setAlive(next);
    };

    const bear = (): void => {
      // Medida a cada nascimento: a largura da pílula muda (o rótulo, o círculo
      // do celular) e o sorteio precisa da forma de agora.
      const layer = layerRef.current;
      const box = { width: layer?.offsetWidth ?? 0, height: layer?.offsetHeight ?? 0 };
      const sparkle = bornSparkle(serial, box, random);
      serial += 1;
      show([...field, sparkle]);
      const death = setTimeout(() => {
        deaths.delete(death);
        show(field.filter((s) => s !== sparkle));
        if (owed) {
          owed = false;
          bear();
        }
      }, sparkle.life);
      deaths.add(death);
      birthTimer = setTimeout(due, Math.round(between(SPARKLE_BIRTH_MIN_MS, SPARKLE_BIRTH_MAX_MS, random())));
    };

    const due = (): void => {
      birthTimer = undefined;
      if (field.length < SPARKLE_MAX_ALIVE) bear();
      else owed = true;
    };

    const pause = (): void => {
      clearTimeout(birthTimer);
      birthTimer = undefined;
      owed = false;
    };

    // De volta à aba, uma nasce logo e a cadência retoma; as que já estavam
    // terminam a vida mesmo com a aba oculta.
    const onVisibility = (): void => {
      if (document.hidden) pause();
      else if (birthTimer === undefined && !owed) due();
    };

    if (!document.hidden) due();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      pause();
      deaths.forEach((death) => clearTimeout(death));
      setAlive([]);
    };
  }, [reduced, random]);

  return (
    <span ref={layerRef} className="shiba-sparkles" aria-hidden="true">
      {(reduced ? RESTING_SPARKLES : alive).map((sparkle) => (
        <svg
          key={sparkle.id}
          className="shiba-sparkle"
          viewBox="0 0 24 24"
          width={sparkle.size}
          height={sparkle.size}
          style={
            {
              left: sparkle.x,
              top: sparkle.y,
              color: sparkle.color,
              '--sparkle-life': sparkle.life === undefined ? undefined : `${sparkle.life}ms`,
            } as CSSProperties
          }
        >
          <path
            fill="currentColor"
            d="M12 0C12.6 6.4 17.6 11.4 24 12C17.6 12.6 12.6 17.6 12 24C11.4 17.6 6.4 12.6 0 12C6.4 11.4 11.4 6.4 12 0Z"
          />
        </svg>
      ))}
    </span>
  );
}
